import { useCallback, useEffect } from 'react';
import { useDataProvider, useStore } from '@ui-framework/index';
import {
  buildMarkNotificationReadApiPath,
  MY_NOTIFICATIONS_READ_ALL_API_PATH,
} from '../apiPaths';
import { unreadCountOf } from '../components/utils';

const applyOptimisticRead = item => ({
  ...item,
  readAt: item.readAt ?? new Date().toISOString(),
  groupUnreadCount: 0,
});

const restoreNotification = (item, captured) => ({
  ...item,
  readAt: captured.readAt,
  groupUnreadCount: captured.groupUnreadCount,
});

const useNotificationInbox = ({
  setNotifications,
  removeOnRead = false,
  reloadOnReadFailure,
  refreshNotifications,
  onMarkAllRead,
  onMarkReadError,
  onMarkAllError,
} = {}) => {
  const store = useStore();
  const provider = useDataProvider(
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default',
  );
  const inboxStore = store.uiState.notificationInboxStore;
  const refresh = useCallback(() => inboxStore.requestRefresh(), [inboxStore]);

  useEffect(() => {
    if (!refreshNotifications) {
      return undefined;
    }
    const requests = new Set();
    const unregister = inboxStore.registerRefresh(async sessionSignal => {
      const controller = new AbortController();
      requests.add(controller);
      const abort = () => controller.abort();
      sessionSignal?.addEventListener('abort', abort, { once: true });
      if (sessionSignal?.aborted) {
        abort();
      }
      try {
        return await refreshNotifications(controller.signal);
      } finally {
        requests.delete(controller);
        sessionSignal?.removeEventListener('abort', abort);
      }
    });
    return () => {
      unregister();
      requests.forEach(controller => controller.abort());
    };
  }, [inboxStore, refreshNotifications]);

  const markRead = useCallback(
    async notification => {
      const notificationId = notification?.id;
      const unread = unreadCountOf(notification);
      if (!notificationId || unread === 0) {
        return true;
      }
      const operationToken = inboxStore.beginNotificationRead(
        notificationId,
        unread,
      );
      if (operationToken === null) {
        return true;
      }

      const captured = {
        readAt: notification.readAt,
        groupUnreadCount: notification.groupUnreadCount,
      };
      if (!removeOnRead) {
        setNotifications?.(current =>
          current.map(item =>
            item.id === notificationId ? applyOptimisticRead(item) : item,
          ),
        );
      }

      const rollback = () => {
        if (!inboxStore.failNotificationRead(notificationId, operationToken)) {
          return false;
        }
        if (removeOnRead) {
          Promise.resolve()
            .then(() => reloadOnReadFailure?.())
            .catch(() => undefined);
          return true;
        }
        setNotifications?.(current =>
          current.map(item =>
            item.id === notificationId
              ? restoreNotification(item, captured)
              : item,
          ),
        );
        return true;
      };

      try {
        const result = await provider.put(
          buildMarkNotificationReadApiPath(notificationId, {
            includeGroup: true,
          }),
          {},
        );
        if (!result?.ok) {
          if (rollback()) {
            onMarkReadError?.(result?.errors);
          }
          return false;
        }
        const body = result?.data?.data ?? result?.data;
        inboxStore.completeNotificationRead(
          notificationId,
          operationToken,
          body?.markedReadCount,
        );
        inboxStore.requestRefresh();
        return true;
      } catch (error) {
        if (rollback()) {
          onMarkReadError?.(error?.result?.errors || error?.errors);
          inboxStore.requestRefresh();
        }
        return false;
      }
    },
    [
      inboxStore,
      onMarkReadError,
      provider,
      reloadOnReadFailure,
      removeOnRead,
      setNotifications,
    ],
  );

  const markAllRead = useCallback(async () => {
    if (!inboxStore.beginMarkAllRead()) {
      return false;
    }

    try {
      const result = await provider.put(MY_NOTIFICATIONS_READ_ALL_API_PATH, {});
      if (!result?.ok) {
        inboxStore.failMarkAllRead();
        onMarkAllError?.(result?.errors);
        inboxStore.requestRefresh();
        return false;
      }
      inboxStore.completeMarkAllRead();
      await onMarkAllRead?.();
      inboxStore.requestRefresh();
      return true;
    } catch (error) {
      inboxStore.failMarkAllRead();
      onMarkAllError?.(error?.result?.errors || error?.errors);
      inboxStore.requestRefresh();
      return false;
    }
  }, [inboxStore, onMarkAllError, onMarkAllRead, provider]);

  return {
    markAllRead,
    markRead,
    markingAllRead: inboxStore.markAllReadPending,
    refresh,
    unreadCount: inboxStore.unreadCount,
  };
};

export default useNotificationInbox;
